import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import EditorForm from "@/design/components/EditorForm";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputPhone from "@/design/forms/InputPhone";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import LineKeyType from "@/utils/enums/LineKeyType";
import ResourceType from "@/utils/enums/ResourceType";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";

import { Alert } from "@mui/material";
import PropTypes from "prop-types";

import lineKeyReducer from "./lineKeyReducer";

function LineKeyForm({ lineKeyTemplate, lineKey, lineKeyTemplateDispatch, deviceLineKeys, specs }) {
  const pbx = useSelector((state) => state.domain.pbx);

  const callParkingStore = useSelector((state) => state.domain.callParkings);
  const conferenceStore = useSelector((state) => state.domain.conferences);
  const customDestinationStore = useSelector((state) => state.domain.customDestinations);
  const dayNightStore = useSelector((state) => state.domain.dayNights);
  const groupStore = useSelector((state) => state.domain.groups);
  const intercomStore = useSelector((state) => state.domain.intercoms);
  const ivrStore = useSelector((state) => state.domain.ivrs);
  const lineKeyTypeStore = useSelector((state) => state.system.lineKeyTypes);
  const mobileStore = useSelector((state) => state.domain.mobile);
  const queueMemberStore = useSelector((state) => state.domain.queueMembers);
  const queueStore = useSelector((state) => state.domain.queues);
  const userStore = useSelector((state) => state.domain.users);
  const voicemailStore = useSelector((state) => state.domain.voicemails);

  const [useDefaultLabel, setUseDefaultLabel] = useState(false);
  const [resourceType, setResourceType] = useState(ResourceType.UNRESTRICTED_NUMBER);
  const [blfQueue, setBlfQueue] = useState("");
  const [blfAccount, setBlfAccount] = useState("");
  const [speedDialQueue, setSpeedDialQueue] = useState("");
  const [speedDialAccount, setSpeedDialAccount] = useState("");
  const [callParking, setCallParking] = useState(undefined);

  const { value, dispatch, setSaveState, formState, errors, allErrors, save } = useSave({
    initialValue: lineKey,
    reducer: lineKeyReducer,
    validator: () => ({
      value: string().required({
        message: "A value is required.",
        condition: !lineKeyIsOfType([LineKeyType.LINE, LineKeyType.EMPTY]),
      }),
      label: string().required({
        message: "A label is required.",
        condition: !(
          lineKeyIsOfType(LineKeyType.EMPTY) ||
          (lineKeyIsOfType(LineKeyType.BLF) && resourceType === ResourceType.USERS && useDefaultLabel)
        ),
      }),
    }),
    onSubmit: () =>
      !lineKeyIsOfType([LineKeyType.EMPTY]) ? Api.storeLineKeyTemplateLineKey(lineKeyTemplate, value) : undefined,
    onSuccess: (response) => {
      if (!value.id) {
        dispatch({ type: "id", value: response.data.lineKey.id });
        lineKeyTemplateDispatch({ type: "addLineKey", value: { ...value, id: response.data.lineKey.id } });
      } else {
        lineKeyTemplateDispatch({ type: "updateLineKey", value });
      }
    },
  });

  const expansionModule = specs?.type.startsWith("EXP");
  const valueTypeId = value.type?.lineKeyId;
  const isSupportedLineKeyType = !!deviceLineKeys.supportedTypes.find(
    (type) => type.lineKeyId === value.type?.lineKeyId,
  );

  const lineKeyIsOfType = useCallback(
    (resourceType, lineKey) => {
      if (Array.isArray(resourceType)) {
        return resourceType.includes(lineKey?.type?.lineKeyId ?? valueTypeId);
      } else {
        return resourceType === (lineKey?.type?.lineKeyId ?? valueTypeId);
      }
    },
    [valueTypeId],
  );

  useEffect(() => {
    // Setup for the form
    startTransition(() => {
      dispatch({ type: "set", value: lineKey });
      setUseDefaultLabel(true);
      setResourceType(ResourceType.UNRESTRICTED_NUMBER);
      setSpeedDialQueue("");
      setSpeedDialAccount("");
      setCallParking(undefined);
    });
  }, [dispatch, lineKey]);

  // On selecting a new line key, refresh the information
  const refreshLineKey = useEffectEvent(() => {
    setUseDefaultLabel(!value.label);

    // Get the Forward/Speed Dial type
    if (lineKeyIsOfType([LineKeyType.FORWARD, LineKeyType.SPEED_DIAL], lineKey)) {
      if (lineKey?.value) {
        // The type is a queue action or unrestricted number (aka things that don't have an actual extension)
        if (lineKey.value?.startsWith("*46*")) {
          setResourceType(ResourceType.QUEUE_PAUSE);
          setSpeedDialQueue(lineKey.value.split("*")[2]);
          setSpeedDialAccount(lineKey.value.split("*")[3]);
        } else {
          Promise.resolve(Api.getExtensionResourceType(lineKey.value.replace(`${pbx.context}-`, "")))
            .then((response) => {
              setResourceType(response);
            })
            .catch(() => {
              setResourceType(ResourceType.UNRESTRICTED_NUMBER);
            });
        }
        setUseDefaultLabel(false);
      }
    } else if (lineKeyIsOfType(LineKeyType.CALL_PARK, lineKey)) {
      if (lineKey?.value) {
        Promise.resolve(Api.getExtension(lineKey.value.replace(`${pbx.context}-`, ""))).then((response) => {
          setCallParking(response.data.parkingLot);
        });
        setUseDefaultLabel(false);
      }
    } else if (lineKeyIsOfType(LineKeyType.BLF, lineKey)) {
      if (lineKey.value?.startsWith(`${pbx.context}-*45*`)) {
        setResourceType(ResourceType.QUEUE_LOGIN);
        setBlfQueue(lineKey.value.split("*")[2]);
        setBlfAccount(lineKey.value.split("*")[3]);
      } else if (lineKey.value?.endsWith("*")) {
        setResourceType(ResourceType.DAY_NIGHT_TOGGLES);
      } else {
        setResourceType(ResourceType.USERS);
        setUseDefaultLabel(lineKey.label === null);
      }
    }
  });

  useEffect(() => {
    startTransition(() => {
      refreshLineKey();
    });
  }, [lineKey.location]);

  function clearLineKey() {
    dispatch({
      type: "set",
      value: {
        location: lineKey.location,
        label: "",
        value: "",
        line: 1,
        extension: "*8",
        type: lineKeyTypeStore.list.find((_lineKeyType) => _lineKeyType.lineKeyId === LineKeyType.EMPTY),
      },
    });

    Promise.resolve(Api.deleteLineKeyTemplateLineKey(lineKeyTemplate, value)).then(() => {
      lineKeyTemplateDispatch({ type: "deleteLineKey", value });
    });
  }

  function lineKeyTypeHandler(property, lineKeyType) {
    if (lineKeyType === LineKeyType.EMPTY) {
      clearLineKey();
    } else {
      dispatch({
        type: "type",
        value: lineKeyTypeStore.list.find((_lineKeyType) => _lineKeyType.lineKeyId === lineKeyType),
      });

      if (lineKeyType === LineKeyType.BLF) {
        setResourceType(ResourceType.USERS);
        setUseDefaultLabel(true);
      } else if (lineKeyType === LineKeyType.FORWARD || lineKeyType === LineKeyType.SPEED_DIAL) {
        setResourceType(ResourceType.UNRESTRICTED_NUMBER);
        setUseDefaultLabel(false);
      } else {
        setUseDefaultLabel(false);
      }
    }
  }

  function formHandler(property, _value) {
    switch (property) {
      case "resourceType":
        setResourceType(_value);

        if (_value === ResourceType.USERS && lineKeyIsOfType([LineKeyType.BLF])) {
          setUseDefaultLabel(true);
        } else {
          setUseDefaultLabel(false);
        }
        break;
      case "defaultLabel":
        setUseDefaultLabel(_value);
        dispatch({
          type: "label",
          value: _value ? null : userStore.list?.find((_user) => _user.username === value.value)?.name || "",
        });
        setSaveState(SaveState.UNSAVED);
        break;
      case "value":
      case "queue":
      case "member": {
        let label = undefined;
        let customValue = null;

        if (lineKeyIsOfType([LineKeyType.FORWARD, LineKeyType.SPEED_DIAL])) {
          if (resourceType === ResourceType.QUEUE_PAUSE) {
            if (property === "queue") {
              setSpeedDialQueue(_value);
            } else {
              setSpeedDialAccount(_value);
            }

            if ((speedDialQueue || property === "queue") && (speedDialAccount || property === "member")) {
              const queueExten = property === "queue" ? _value : speedDialQueue;
              const memberExten = property === "member" ? _value : speedDialAccount;

              if (queueExten && memberExten) {
                customValue = `*46*${queueExten}*${memberExten}`;
                label = `Pause ${queueExten}`;
              }
            }
          } else {
            switch (resourceType) {
              case ResourceType.USERS:
                label = userStore.list.find((_user) => _user.exten === _value).name;
                break;
              case ResourceType.MOBILE_USERS:
                label = mobileStore.list.find((_mobile) => _mobile.exten === _value).name;
                break;
              case ResourceType.GROUPS:
                label = groupStore.list.find((_group) => _group.exten === _value)?.name;
                break;
              case ResourceType.IVRS:
                label = ivrStore.list.find((_ivr) => _ivr.exten === _value)?.name;
                break;
              case ResourceType.QUEUES:
                label = queueStore.list.find((_queue) => _queue.exten === _value)?.name;
                break;
              case ResourceType.DAY_NIGHTS:
                label = dayNightStore.list.find((_dayNight) => _dayNight.exten === _value)?.name;
                break;
              case ResourceType.CALL_PARKINGS:
                label = callParkingStore.list.find((_callParking) => _callParking.exten === _value)?.name;
                break;
              case ResourceType.VOICEMAILS:
                label = voicemailStore.list.find((_voicemail) => _voicemail.exten === _value)?.name;
                break;
              case ResourceType.CONFERENCES:
                label = conferenceStore.list.find((_conference) => _conference.exten === _value)?.name;
                break;
              case ResourceType.INTERCOMS:
                label = intercomStore.list.find((_intercom) => _intercom.exten === _value)?.name;
                break;
              case ResourceType.CUSTOM_DESTINATIONS:
                label = customDestinationStore.list.find(
                  (_customDestination) => _customDestination.exten === _value,
                )?.name;
                break;
            }
          }
        } else if (lineKeyIsOfType([LineKeyType.CALL_PARK]) && _value) {
          customValue = `${pbx.context}-${_value}`;
          label = `${callParking?.name} \u00b7 ${_value}`;
        } else if (lineKeyIsOfType([LineKeyType.BLF])) {
          switch (resourceType) {
            case ResourceType.USERS:
              label = userStore.list.find((_user) => _user.username === _value).name;
              break;
            case ResourceType.DAY_NIGHT_TOGGLES:
              customValue = `${pbx.context}-${_value}`;
              label = dayNightStore.list.find((_dayNight) => _dayNight.switchExten === _value).name;
              break;
            case ResourceType.QUEUE_LOGIN:
              if (property === "queue") {
                setBlfQueue(_value);
              } else {
                setBlfAccount(_value);
              }

              if ((blfQueue || property === "queue") && (blfAccount || property === "member")) {
                const queueExten = property === "queue" ? _value : blfQueue;
                const memberExten = property === "member" ? _value : blfAccount;

                if (queueExten && memberExten) {
                  customValue = `${pbx.context}-*45*${queueExten}*${memberExten}`;
                  label = `Login ${queueExten}`;
                }
              }
          }
        }

        dispatch({ type: "value", value: customValue ?? _value, label: useDefaultLabel ? null : label });
        setSaveState(SaveState.UNSAVED);
        break;
      }
      default:
        dispatch({ type: property, value: _value });
        setSaveState(SaveState.UNSAVED);
    }
  }

  const fieldMeta = useMemo(() => {
    switch (resourceType) {
      case ResourceType.USERS:
        return { list: userStore.list, label: "User" };
      case ResourceType.MOBILE_USERS:
        return { list: mobileStore.list, label: "Mobile user" };
      case ResourceType.GROUPS:
        return { list: groupStore.list, label: "Group" };
      case ResourceType.IVRS:
        return { list: ivrStore.list, label: "IVR" };
      case ResourceType.QUEUES:
        return { list: queueStore.list, label: "Queue" };
      case ResourceType.DAY_NIGHTS:
        return { list: dayNightStore.list, label: "Switch" };
      case ResourceType.CALL_PARKINGS:
        return { list: callParkingStore.list, label: "Call parking" };
      case ResourceType.VOICEMAILS:
        return { list: voicemailStore.list, label: "Voicemail" };
      case ResourceType.CONFERENCES:
        return { list: conferenceStore.list, label: "Conference" };
      case ResourceType.INTERCOMS:
        return { list: intercomStore.list, label: "Intercom" };
      case ResourceType.CUSTOM_DESTINATIONS:
        return { list: customDestinationStore.list, label: "Custom destination" };
    }
  }, [
    resourceType,
    userStore.list,
    mobileStore.list,
    groupStore.list,
    ivrStore.list,
    queueStore.list,
    dayNightStore.list,
    callParkingStore.list,
    voicemailStore.list,
    conferenceStore.list,
    intercomStore.list,
    customDestinationStore.list,
  ]);

  return (
    <EditorForm
      title={`Line key ${lineKey?.location}`}
      action={
        <SaveIndicator
          formState={formState}
          errors={allErrors}
          save={save}
          disabled={lineKeyIsOfType([LineKeyType.EMPTY])}
          mini
        />
      }
    >
      <InputSelect
        id="lineKeyType"
        label="Type"
        onChange={lineKeyTypeHandler}
        value={value.type?.lineKeyId ?? 0}
        selectValue="lineKeyId"
        selects={lineKeyTypeStore.list}
        getOptionLabel={(option) => option.type}
        error={errors}
      />

      {expansionModule && (
        <Alert severity="info" sx={{ boxShadow: 1 }}>
          Which line key type is supported by the expansion module depends on the phone it is connected to.
        </Alert>
      )}

      {!isSupportedLineKeyType && !expansionModule && (
        <Alert severity="warning" sx={{ boxShadow: 1 }}>
          This line key type is not supported on your preview device. You can still set it, but it may not work on your
          device.
        </Alert>
      )}

      {lineKeyIsOfType([LineKeyType.BLF, LineKeyType.FORWARD, LineKeyType.SPEED_DIAL]) && (
        <InputSelect
          required
          id="resourceType"
          label="Resource"
          onChange={formHandler}
          value={resourceType}
          selectValue="value"
          selects={
            value.type.lineKeyId === 16
              ? [
                  { value: 2, title: "User" },
                  { value: 9, title: "Queue login" },
                  { value: 16, title: "Toggle switch" },
                ]
              : value.type.lineKeyId === 2
                ? [
                    { value: 2, title: "User" },
                    { value: 17, title: "Mobile users" },
                    { value: 3, title: "Group" },
                    { value: 8, title: "IVRs" },
                    { value: 7, title: "Queues" },
                    { value: 15, title: "Switch" },
                    { value: 6, title: "Voicemails" },
                    { value: 12, title: "Conferences" },
                    { value: 14, title: "Intercom" },
                    { value: 11, title: "Custom destinations" },
                    { value: 1, title: "Unrestricted number" },
                  ]
                : [
                    { value: 2, title: "User" },
                    { value: 17, title: "Mobile users" },
                    { value: 3, title: "Group" },
                    { value: 8, title: "IVRs" },
                    { value: 7, title: "Queues" },
                    { value: 10, title: "Queue pause" },
                    { value: 15, title: "Switch" },
                    { value: 13, title: "Call Parking" },
                    { value: 12, title: "Conferences" },
                    { value: 14, title: "Intercom" },
                    { value: 11, title: "Custom destination" },
                    { value: 1, title: "Unrestricted number" },
                  ]
          }
          getOptionLabel={(option) => option.title}
          error={errors}
        />
      )}

      {lineKeyIsOfType(LineKeyType.BLF) && (
        <>
          {resourceType === ResourceType.QUEUE_LOGIN ? (
            <>
              <InputSelect
                required
                id="queue"
                label="Queue"
                onChange={formHandler}
                errors={errors.value}
                value={blfQueue ?? ""}
                selectValue="exten"
                selects={queueStore.list}
                getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
              />
              <InputSelect
                required
                id="member"
                label="Queue member"
                onChange={formHandler}
                errors={errors.value}
                value={blfAccount ?? ""}
                selectValue="exten"
                selects={queueMemberStore.list.map((member) => ({ type: member.type, ...member.attributes }))}
                getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
              />
            </>
          ) : resourceType === ResourceType.USERS ? (
            <>
              <InputSelect
                id="value"
                label="User"
                onChange={formHandler}
                value={value?.value || ""}
                selectValue="username"
                selects={userStore.list}
                getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
                error={errors}
              />
              <InputToggle
                disabled={!value?.value}
                id="defaultLabel"
                onChange={formHandler}
                states={[false, true]}
                value={useDefaultLabel}
                primary="Use default label"
                secondary="Use the default label for this user."
              />
            </>
          ) : (
            <InputSelect
              required
              id="value"
              label="Switch"
              onChange={formHandler}
              value={value?.value.replace(`${pbx.context}-`, "") || ""}
              selectValue="switchExten"
              selects={dayNightStore.list}
              getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
              error={errors}
            />
          )}
        </>
      )}

      {lineKeyIsOfType([LineKeyType.FORWARD, LineKeyType.SPEED_DIAL]) && (
        <>
          {resourceType === ResourceType.QUEUE_PAUSE ? (
            <>
              <InputSelect
                required
                id="queue"
                label="Queue"
                onChange={formHandler}
                errors={errors.value}
                value={speedDialQueue ?? ""}
                selectValue="exten"
                selects={queueStore.list}
                getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
              />
              <InputSelect
                required
                id="member"
                label="Queue member"
                onChange={formHandler}
                errors={errors.value}
                value={speedDialAccount ?? ""}
                selectValue="exten"
                selects={queueMemberStore.list.map((member) => ({ type: member.type, ...member.attributes }))}
                getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
              />
            </>
          ) : resourceType === ResourceType.UNRESTRICTED_NUMBER ? (
            <InputPhone
              specialChars
              required
              id="value"
              label="Number"
              onChange={formHandler}
              value={value?.value || ""}
              error={errors}
            />
          ) : (
            <InputSelect
              required
              id="value"
              label={fieldMeta?.label}
              onChange={formHandler}
              value={value?.value.replace(`${pbx.context}-`, "") || ""}
              selectValue="exten"
              selects={fieldMeta?.list}
              getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
              error={errors}
            />
          )}
        </>
      )}

      {lineKeyIsOfType(LineKeyType.CALL_PARK) && (
        <>
          <InputSelect
            required
            id="callParking"
            label="Call parking"
            onChange={(e, value) =>
              Promise.resolve(Api.getExtension(value)).then((response) => {
                setCallParking(response.data.parkingLot);
              })
            }
            errors={errors.value}
            value={callParking?.exten}
            selectValue="exten"
            selects={callParkingStore.list}
            getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
          />
          {callParking?.spaceExtensions && (
            <InputSelect
              required
              id="value"
              label="Parking space"
              onChange={formHandler}
              errors={errors.value}
              value={value.value.replace(`${pbx.context}-`, "")}
              selectValue="exten"
              selects={callParking.spaceExtensions}
              getOptionLabel={(option) => option.exten}
            />
          )}
        </>
      )}

      {lineKeyIsOfType([LineKeyType.URL, LineKeyType.OPEN_DOOR, LineKeyType.VIDEO_MONITORING]) && (
        <InputText required id="value" label="URL" onChange={formHandler} value={value?.value || ""} error={errors} />
      )}

      {lineKeyIsOfType([LineKeyType.DTMF]) && (
        <InputText
          required
          id="value"
          label="Number"
          onChange={formHandler}
          value={value?.value || ""}
          error={errors}
        />
      )}

      {value?.type.lineKeyId !== LineKeyType.EMPTY &&
        !(value?.type.lineKeyId === LineKeyType.BLF && resourceType === ResourceType.USERS && useDefaultLabel) && (
          <InputText
            required={!!value && value.type.lineKeyId !== 0}
            id="label"
            label="Label"
            onChange={formHandler}
            value={value?.label ?? ""}
            error={errors}
          />
        )}
    </EditorForm>
  );
}

LineKeyForm.propTypes = {
  lineKeyTemplate: PropTypes.object,
  lineKey: PropTypes.object,
  lineKeyTemplateDispatch: PropTypes.func,
  deviceLineKeys: PropTypes.object,
  specs: PropTypes.object,
};

export default LineKeyForm;
